import { apiClient } from '@shared/api';
import type { ApiResponse } from '@shared/types';
import type {
  MatchingResult,
  MasterDataStats,
  MasterDataSyncResult,
  MasterDataRecordType,
  MatchedLineItem,
  RematchLineItemRequest,
  CustomerOption,
  TermOption,
  ProjectCategoryOption,
  SalesRepOption,
  SalesCoordinatorOption,
  BaseMarkup,
} from '../types';

class QuotationIntakeService {
  private readonly baseUrl = '/quotation-intake';

  async extractFromText(text: string): Promise<MatchingResult> {
    const { data } = await apiClient.post<ApiResponse<MatchingResult>>(
      `${this.baseUrl}/extract-text`,
      { text },
      { timeout: 120000 },
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Extraction failed');
  }

  async extractFromFile(file: File): Promise<MatchingResult> {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await apiClient.post<ApiResponse<MatchingResult>>(
      `${this.baseUrl}/extract-file`,
      formData,
      {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000,
      },
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Extraction failed');
  }

  /**
   * Combined manual intake: pasted text + N files/screenshots in ONE call (ExtractFromEmailAsync),
   * so the model sees every source together and merges duplicates across them — text is never
   * dropped (86d3hdy52).
   *
   * 14ykpqtw3hw: the quotation-scoped twin of the shared /api/intake/extract — same extraction and
   * matching, then the cutting-list quantity rule (whole pieces, kg read against the RFQ). Sales
   * Orders keep calling the shared route, which stays unchanged (QUO-R-007).
   */
  async extractCombined(text: string, files: File[]): Promise<MatchingResult> {
    const trimmed = text.trim();
    const formData = new FormData();
    if (trimmed.length > 0) formData.append('text', trimmed);
    for (const file of files) formData.append('files', file);

    const { data } = await apiClient.post<ApiResponse<MatchingResult>>(
      `${this.baseUrl}/extract`,
      formData,
      { headers: { 'Content-Type': 'multipart/form-data' }, timeout: 180000 },
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Extraction failed');
  }

  async rematchLineItem(request: RematchLineItemRequest): Promise<MatchedLineItem> {
    const { data } = await apiClient.post<ApiResponse<MatchedLineItem>>(
      `${this.baseUrl}/re-match`,
      request,
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Re-match failed');
  }

  async syncMasterData(): Promise<MasterDataSyncResult> {
    const { data } = await apiClient.post<ApiResponse<MasterDataSyncResult>>(
      `${this.baseUrl}/sync-master-data`,
      null,
      { timeout: 120000 },
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Sync failed');
  }

  async syncRebarItems(): Promise<MasterDataSyncResult> {
    const { data } = await apiClient.post<ApiResponse<MasterDataSyncResult>>(
      `${this.baseUrl}/sync-master-data/items`,
      null,
      { timeout: 120000 },
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Rebar items sync failed');
  }

  async syncCustomers(): Promise<MasterDataSyncResult> {
    const { data } = await apiClient.post<ApiResponse<MasterDataSyncResult>>(
      `${this.baseUrl}/sync-master-data/customers`,
      null,
      { timeout: 120000 },
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Customer sync failed');
  }

  /**
   * Admin force-sync. Calls the master-data admin endpoint with the matching record-type
   * discriminator (see backend `MasterDataRecordTypes`). Requires `quotations.sync_master_data`
   * permission — the backend will 403 otherwise. Used by the MasterDataConfigDialog's
   * "Full Sync" buttons for the 4 audited caches (Customers, Trucking Rates, Brackets,
   * Base Markup).
   */
  async forceSync(recordType: MasterDataRecordType): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      `/admin/cache/force-sync`,
      { recordType },
      { timeout: 120000 },
    );
    if (!data.success) throw new Error(data.message || 'Force sync failed');
  }

  async syncTerms(): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      `${this.baseUrl}/sync-master-data/terms`,
      null,
      { timeout: 60000 },
    );
    if (!data.success) throw new Error(data.message || 'Terms sync failed');
  }

  async getMasterDataStats(): Promise<MasterDataStats> {
    const { data } = await apiClient.get<ApiResponse<MasterDataStats>>(
      `${this.baseUrl}/master-data/stats`,
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch stats');
  }

  async searchCustomers(query: string, take = 20): Promise<CustomerOption[]> {
    const { data } = await apiClient.get<ApiResponse<CustomerOption[]>>(
      `${this.baseUrl}/customers/search`,
      { params: { q: query, take } },
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Customer search failed');
  }

  async getTerms(): Promise<TermOption[]> {
    const { data } = await apiClient.get<ApiResponse<TermOption[]>>(
      `${this.baseUrl}/terms`,
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch terms');
  }

  async getProjectCategories(): Promise<ProjectCategoryOption[]> {
    const { data } = await apiClient.get<ApiResponse<ProjectCategoryOption[]>>(
      `${this.baseUrl}/project-categories`,
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch project categories');
  }

  async syncProjectCategories(): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      `${this.baseUrl}/sync-master-data/project-categories`,
      null,
      { timeout: 60000 },
    );
    if (!data.success) throw new Error(data.message || 'Project categories sync failed');
  }

  async getSalesReps(): Promise<SalesRepOption[]> {
    const { data } = await apiClient.get<ApiResponse<SalesRepOption[]>>(
      `${this.baseUrl}/sales-reps`,
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch sales reps');
  }

  async syncSalesReps(): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      `${this.baseUrl}/sync-master-data/sales-reps`,
      null,
      { timeout: 60000 },
    );
    if (!data.success) throw new Error(data.message || 'Sales reps sync failed');
  }

  async getSalesCoordinators(): Promise<SalesCoordinatorOption[]> {
    const { data } = await apiClient.get<ApiResponse<SalesCoordinatorOption[]>>(
      `${this.baseUrl}/sales-coordinators`,
    );
    if (data.success && data.data) return data.data;
    throw new Error(data.message || 'Failed to fetch sales coordinators');
  }

  async syncSalesCoordinators(): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      `${this.baseUrl}/sync-master-data/sales-coordinators`,
      null,
      { timeout: 60000 },
    );
    if (!data.success) throw new Error(data.message || 'Sales coordinators sync failed');
  }

  async syncBrackets(): Promise<void> {
    const { data } = await apiClient.post<ApiResponse<unknown>>(
      `${this.baseUrl}/sync-master-data/brackets`,
      null,
      { timeout: 60000 },
    );
    if (!data.success) throw new Error(data.message || 'Markup brackets sync failed');
  }

  async getBaseMarkup(): Promise<BaseMarkup | null> {
    const { data } = await apiClient.get<ApiResponse<BaseMarkup | null>>(
      `${this.baseUrl}/base-markup`,
    );
    if (!data.success) throw new Error(data.message || 'Failed to fetch base markup');
    return data.data ?? null;
  }
}

export const quotationIntakeService = new QuotationIntakeService();
